import React, { useState, useRef, useEffect } from 'react';
import { Input } from 'antd-mobile';
import { CheckOutlined } from '@ant-design/icons';

interface AutoCompleteOption {
  label: string;
  value: string;
}

interface AutoCompleteProps {
  className?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onSelect?: (value: string) => void;
  options: AutoCompleteOption[];
  disabled?: boolean;
  autoSelected?: boolean; // 新增：标记是否为自动选择
  selectedValues?: string[]; // 新增：已选择的值列表，用于显示对勾
  id?: string; // 输入框 id，避免多个输入互相影响
  name?: string; // 输入框 name
  /** 为 true 时，获得焦点即展示全部选项（无输入也展示） */
  showAllOnFocus?: boolean;
}

export const AutoComplete: React.FC<AutoCompleteProps> = ({
  className = '',
  placeholder,
  value = '',
  onChange,
  onSelect,
  options = [],
  disabled = false,
  autoSelected = false,
  selectedValues = [],
  id,
  name,
  showAllOnFocus = false,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [isSelected, setIsSelected] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setInputValue(value);
    // 如果是自动选择的结果，标记为已选中
    if (autoSelected && value) {
      setIsSelected(true);
    } else if (!isSelected) {
      setIsSelected(false);
    }
  }, [value, autoSelected]);

  // 仅在本输入框获得焦点时根据输入和选项决定是否显示下拉，避免多个输入框互相触发
  useEffect(() => {
    if (!hasFocus || options.length === 0 || isSelected) {
      setShowOptions(false);
      return;
    }
    const hasInput = inputValue.trim().length > 0;
    if (hasInput || showAllOnFocus) {
      setShowOptions(true);
    } else {
      setShowOptions(false);
    }
  }, [hasFocus, options, inputValue, isSelected, showAllOnFocus]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const handleInputChange = (val: string) => {
    setInputValue(val);
    setIsSelected(false); // 重置选择状态
    onChange?.(val);
    
    // 如果有输入值且有选项，显示下拉
    if (val.trim().length > 0 && options.length > 0) {
      setShowOptions(true);
    } else {
      setShowOptions(false);
    }
  };

  const handleInputFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setHasFocus(true);
  };

  const handleInputBlur = () => {
    setHasFocus(false);
    blurTimeoutRef.current = setTimeout(() => setShowOptions(false), 150);
  };

  const handleOptionClick = (option: AutoCompleteOption) => {
    setShowOptions(false); // 隐藏下拉
    setIsSelected(false); // 重置选中状态，允许继续输入
    if (onSelect) {
      onSelect(option.value); // 如果有 onSelect 回调，使用它
    } else {
      setInputValue(option.value);
      setIsSelected(true); // 标记为已选中
      onChange?.(option.value);
    }
  };

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
    option.value.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <Input
        id={id}
        name={name}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        disabled={disabled}
      />
      
      {showOptions && filteredOptions.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.map((option, index) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <div
                key={`${option.value}-${index}`}
                className="px-3 py-2 cursor-pointer hover:bg-gray-50 active:bg-gray-100 border-b border-gray-100 last:border-b-0 flex items-center justify-between"
                onClick={() => handleOptionClick(option)}
              >
                <div className="flex-1">
                  <div className="text-sm text-gray-900">{option.label}</div>
                  {option.label !== option.value && (
                    <div className="text-xs text-gray-500 mt-1">{option.value}</div>
                  )}
                </div>
                {isSelected && (
                  <CheckOutlined className="text-blue-500 text-base ml-2 flex-shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
